import { useState, useEffect } from 'react';
import { remindersAPI, quoteRemindersAPI } from '../utils/api';
import { logger } from '../utils/logger';

// ============================================
// INTERFACES BACKEND (Prisma ReminderPolicy)
// ============================================

export interface ReminderLevel {
  daysAfter: number;
  emailTemplateId?: string; // Optionnel - soit emailTemplateId soit emailTemplate inline
  emailTemplate?: {
    id?: string; // Optionnel pour templates inline
    name?: string;
    subject: string;
    body: string;
  };
}

export interface ReminderPolicy {
  id: string;
  tenantId: string;
  companyId: string;
  entityType: 'invoice' | 'quote';
  isActive: boolean;
  levels: ReminderLevel[];
  sendCopyToCompany: boolean;
  autoMarkOverdue?: boolean; // Pour factures uniquement
  stopOnExpiry?: boolean; // Pour devis uniquement
  createdAt: string;
  updatedAt: string;
}

// ============================================
// TYPES POUR CRÉATION/MISE À JOUR
// ============================================

export interface CreatePolicyData {
  entityType: 'invoice' | 'quote';
  isActive: boolean;
  levels: ReminderLevel[];
  sendCopyToCompany: boolean;
  autoMarkOverdue?: boolean;
  stopOnExpiry?: boolean;
}

export interface UpdatePolicyData {
  isActive?: boolean;
  levels?: ReminderLevel[];
  sendCopyToCompany?: boolean;
  autoMarkOverdue?: boolean;
  stopOnExpiry?: boolean;
}

// ============================================
// CUSTOM HOOK PRINCIPAL
// ============================================

export const useReminderPolicies = (entityType: 'invoice' | 'quote') => {
  // État local
  const [policies, setPolicies] = useState<ReminderPolicy[]>([]);
  const [currentPolicy, setCurrentPolicy] = useState<ReminderPolicy | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ============================================
  // CHARGEMENT DES POLICIES
  // ============================================

  const loadPolicies = async () => {
    setLoading(true);
    setError(null);

    try {
      const api = entityType === 'invoice' ? remindersAPI : quoteRemindersAPI;
      const response = await api.getPolicies();

      // Filtrer par entityType
      const filteredPolicies = (response.data || response).filter(
        (p: ReminderPolicy) => p.entityType === entityType
      );

      setPolicies(filteredPolicies);

      // Si une seule policy, la définir comme courante
      if (filteredPolicies.length === 1) {
        setCurrentPolicy(filteredPolicies[0]);
      }

      return true;
    } catch (err: any) {
      logger.error(`Erreur chargement policies ${entityType}:`, err);
      setError(`Erreur lors du chargement des paramètres de ${entityType === 'invoice' ? 'factures' : 'devis'}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // CHARGEMENT D'UNE POLICY PAR ID
  // ============================================

  const loadPolicyById = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const api = entityType === 'invoice' ? remindersAPI : quoteRemindersAPI;
      const response = await api.getPolicyById(id);
      const policy = response.data || response;

      setCurrentPolicy(policy);
      return policy;
    } catch (err: any) {
      logger.error(`Erreur chargement policy ${id}:`, err);
      setError('Erreur lors du chargement de la policy');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // CRÉATION D'UNE POLICY
  // ============================================

  const createPolicy = async (data: CreatePolicyData) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const api = entityType === 'invoice' ? remindersAPI : quoteRemindersAPI;
      const response = await api.createPolicy(data);
      const newPolicy = response.data || response;

      setPolicies([...policies, newPolicy]);
      setCurrentPolicy(newPolicy);

      setSuccessMessage(`Paramètres de ${entityType === 'invoice' ? 'factures' : 'devis'} créés avec succès`);
      setTimeout(() => setSuccessMessage(null), 3000);

      return newPolicy;
    } catch (err: any) {
      logger.error(`Erreur création policy ${entityType}:`, err);
      setError(`Erreur lors de la création des paramètres`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // MISE À JOUR D'UNE POLICY
  // ============================================

  const updatePolicy = async (id: string, data: UpdatePolicyData) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const api = entityType === 'invoice' ? remindersAPI : quoteRemindersAPI;
      const response = await api.updatePolicy(id, data);
      const updatedPolicy = response.data || response;

      // Mettre à jour dans la liste
      setPolicies(policies.map(p => p.id === id ? updatedPolicy : p));

      // Mettre à jour la policy courante si c'est celle-ci
      if (currentPolicy?.id === id) {
        setCurrentPolicy(updatedPolicy);
      }

      setSuccessMessage(`Paramètres de ${entityType === 'invoice' ? 'factures' : 'devis'} mis à jour avec succès`);
      setTimeout(() => setSuccessMessage(null), 3000);

      return updatedPolicy;
    } catch (err: any) {
      logger.error(`Erreur mise à jour policy ${id}:`, err);
      setError(`Erreur lors de la mise à jour des paramètres`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // SUPPRESSION D'UNE POLICY
  // ============================================

  const deletePolicy = async (id: string) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const api = entityType === 'invoice' ? remindersAPI : quoteRemindersAPI;
      await api.deletePolicy(id);

      // Retirer de la liste
      setPolicies(policies.filter(p => p.id !== id));

      // Réinitialiser la policy courante si c'était celle-ci
      if (currentPolicy?.id === id) {
        setCurrentPolicy(null);
      }

      setSuccessMessage('Paramètres supprimés avec succès');
      setTimeout(() => setSuccessMessage(null), 3000);

      return true;
    } catch (err: any) {
      logger.error(`Erreur suppression policy ${id}:`, err);
      setError('Erreur lors de la suppression des paramètres');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ACTIVATION/DÉSACTIVATION D'UNE POLICY
  // ============================================

  const togglePolicy = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const api = entityType === 'invoice' ? remindersAPI : quoteRemindersAPI;
      const response = await api.togglePolicy(id);
      const updatedPolicy = response.data || response;

      // Mettre à jour dans la liste
      setPolicies(policies.map(p => p.id === id ? updatedPolicy : p));

      // Mettre à jour la policy courante si c'est celle-ci
      if (currentPolicy?.id === id) {
        setCurrentPolicy(updatedPolicy);
      }

      const status = updatedPolicy.isActive ? 'activés' : 'désactivés';
      setSuccessMessage(`Paramètres ${status} avec succès`);
      setTimeout(() => setSuccessMessage(null), 3000);

      return updatedPolicy;
    } catch (err: any) {
      logger.error(`Erreur toggle policy ${id}:`, err);
      setError('Erreur lors de la modification du statut');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HELPERS POUR MISE À JOUR LOCALE
  // ============================================

  const updateCurrentPolicy = (updates: Partial<ReminderPolicy>) => {
    if (!currentPolicy) return;
    setCurrentPolicy({ ...currentPolicy, ...updates });
  };

  // ============================================
  // HELPER POUR OBTENIR OU CRÉER LA POLICY
  // ============================================

  const getOrCreatePolicy = async (): Promise<ReminderPolicy | null> => {
    // Si on a déjà une policy courante, la retourner
    if (currentPolicy) {
      return currentPolicy;
    }

    // Si on a des policies, prendre la première
    if (policies.length > 0) {
      setCurrentPolicy(policies[0]);
      return policies[0];
    }

    // Sinon, créer une policy par défaut
    const defaultData: any = {
      entityType,
      levels: entityType === 'invoice'
        ? [
            {
              daysAfter: 7,
              emailTemplate: {
                subject: 'Rappel de paiement - Facture {{invoiceNumber}}',
                body: 'Bonjour {{clientName}},\n\nNous vous rappelons que la facture {{invoiceNumber}} d\'un montant de {{amount}} arrive à échéance le {{dueDate}}.\n\nMerci de procéder au règlement dans les plus brefs délais.\n\nCordialement,\n{{companyName}}'
              }
            },
            {
              daysAfter: 14,
              emailTemplate: {
                subject: 'Relance - Facture {{invoiceNumber}} en retard',
                body: 'Bonjour {{clientName}},\n\nNous constatons que la facture {{invoiceNumber}} d\'un montant de {{amount}} est en retard de {{daysOverdue}} jours.\n\nMerci de régulariser cette situation rapidement.\n\nCordialement,\n{{companyName}}'
              }
            },
            {
              daysAfter: 21,
              emailTemplate: {
                subject: 'Dernière relance - Facture {{invoiceNumber}}',
                body: 'Bonjour {{clientName}},\n\nMalgré nos précédentes relances, la facture {{invoiceNumber}} d\'un montant de {{amount}} reste impayée ({{daysOverdue}} jours de retard).\n\nSans règlement sous 7 jours, nous serons contraints d\'engager une procédure de recouvrement.\n\nCordialement,\n{{companyName}}'
              }
            },
          ]
        : [
            {
              daysAfter: 7,
              emailTemplate: {
                subject: 'Relance - Devis {{quoteNumber}}',
                body: 'Bonjour {{clientName}},\n\nNous souhaitions avoir votre retour concernant le devis {{quoteNumber}} que nous vous avons transmis.\n\nRestez-vous intéressé par notre proposition ?\n\nCordialement,\n{{companyName}}'
              }
            },
            {
              daysAfter: 14,
              emailTemplate: {
                subject: 'Devis {{quoteNumber}} - Avez-vous des questions ?',
                body: 'Bonjour {{clientName}},\n\nNous n\'avons pas encore reçu de réponse concernant le devis {{quoteNumber}}.\n\nAvez-vous des questions ou souhaitez-vous que nous ajustions notre proposition ?\n\nNous restons à votre disposition.\n\nCordialement,\n{{companyName}}'
              }
            },
            {
              daysAfter: 21,
              emailTemplate: {
                subject: 'Dernière relance - Devis {{quoteNumber}}',
                body: 'Bonjour {{clientName}},\n\nLe devis {{quoteNumber}} arrive bientôt à expiration ({{expiryDate}}).\n\nSi vous souhaitez donner suite à ce projet, merci de nous confirmer votre accord rapidement.\n\nCordialement,\n{{companyName}}'
              }
            },
          ],
    };

    return await createPolicy(defaultData);
  };

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================

  useEffect(() => {
    loadPolicies();
  }, [entityType]);

  // ============================================
  // RETOUR DE L'INTERFACE PUBLIQUE DU HOOK
  // ============================================

  return {
    // État
    policies,
    currentPolicy,
    loading,
    error,
    successMessage,

    // Setters
    setCurrentPolicy,
    setError,
    setSuccessMessage,
    updateCurrentPolicy,

    // Actions CRUD
    loadPolicies,
    loadPolicyById,
    createPolicy,
    updatePolicy,
    deletePolicy,
    togglePolicy,

    // Helpers
    getOrCreatePolicy,
  };
};
